import React from 'react';

interface AboutPageProps {
  onNavigate?: (path: string, view: string, tab?: string) => void;
}

export const AboutPage: React.FC<AboutPageProps> = ({ onNavigate }) => {
  const handleNav = (path: string, view: string, tab: string = 'water') => {
    if (onNavigate) {
      onNavigate(path, view, tab);
    } else {
      window.location.href = path;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <article className="bg-white dark:bg-[#1F2937] rounded-3xl p-6 sm:p-10 lg:p-12 space-y-8 shadow-xs border border-slate-200 dark:border-slate-700">
        <header className="border-b border-slate-100 dark:border-slate-700/80 pb-6">
          <span className="text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full bg-teal-50 dark:bg-teal-900/40 text-[#087F9C] dark:text-[#39BFD3] border border-teal-200/60 dark:border-teal-800/60 inline-block mb-3">
            About the Platform
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D] dark:text-white tracking-tight">
            About PipeSizingCalculator.com
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
            Independent mechanical engineering tools for hydraulic pipe sizing, friction loss analysis, and fluid flow verification.
          </p>
        </header>

        {/* Section 1: What is Pipe Sizing Calculator */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            1. What Is Pipe Sizing Calculator?
          </h2>
          <p>
            PipeSizingCalculator.com is an open-access engineering calculation platform developed specifically for building services consultants, mechanical contractors, public health engineers, plumbing designers, and engineering students. The platform provides fast, deterministic, client-side sizing solvers and hydraulic reference tables based on recognized international piping standards.
          </p>
          <p>
            Unlike traditional proprietary engineering software suites that require costly licenses, complex installations, or cloud compute queues, our suite runs entirely in modern web browsers. Calculations execute instantly with zero server roundtrips, allowing engineers to verify pipe bore requirements, fluid velocities, and pressure drops directly on site, in design meetings, or at their workstations.
          </p>
        </section>

        {/* Section 2: Why This Website Was Created */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            2. Why This Website Was Created
          </h2>
          <p>
            Piping system design in mechanical and public health engineering often involves repetitive manual calculations: converting between volumetric flow and mass flow, looking up temperature-dependent fluid kinematic viscosities, looking up internal diameters across various pipe schedules, solving the implicit Colebrook-White friction factor, and cross-checking velocity against acoustic and erosion thresholds.
          </p>
          <p>
            Engineers frequently rely on disjointed spreadsheets or thumb-rule nomograms that can be error-prone or outdated. We built PipeSizingCalculator.com to consolidate these workflows into an accessible, clean, and reliable toolset that combines:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <strong>Rigorous hydraulic physics:</strong> Utilizing the fundamental Darcy-Weisbach equation and Swamee-Jain explicit solutions rather than simplified empirical approximations that break down across varied temperatures.
            </li>
            <li>
              <strong>Real-world manufacturing standards:</strong> Implementing exact internal bores from published schedule tables rather than nominal diameters (e.g., distinguishing between nominal 50mm and exact internal dimensions for copper, carbon steel, and PVC).
            </li>
            <li>
              <strong>Simultaneous multi-unit calculations:</strong> Supporting seamless switching between Metric (SI: mm, m/s, kPa, L/s) and Imperial (US/UK: in, ft/s, psi, GPM) units without manual conversion errors.
            </li>
          </ul>
        </section>

        {/* Section 3: Engineering Calculation Modules Available */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            3. Engineering Calculation Tools Provided
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-2">
              <h3 className="font-display font-bold text-base text-[#10283D] dark:text-white">
                💧 Water Pipe Sizing
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Sizes commercial domestic water, heating, and chilled distribution mains across 8 pipe materials according to BS EN 806 and CIBSE Guide G velocity limits.
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-2">
              <h3 className="font-display font-bold text-base text-[#10283D] dark:text-white">
                🪨 Copper Pipe Sizing
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Specialized module for BS EN 1057 (Table X) and ASTM B88 copper tubing with strict CIBSE erosion caps for domestic and hydronic circuits.
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-2">
              <h3 className="font-display font-bold text-base text-[#10283D] dark:text-white">
                ⚡ Flow Rate Solver
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Instant multi-unit flow rate converter solving fluid capacity across L/s, m³/h, US GPM, and UK GPM simultaneously based on bore and target velocity.
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-2">
              <h3 className="font-display font-bold text-base text-[#10283D] dark:text-white">
                📉 Pressure Drop (Darcy)
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Calculates Darcy friction factor, Reynolds number, hydraulic gradient (kPa/100m or psi/100ft), and minor fitting loss across piping runs.
              </p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-2 sm:col-span-2">
              <h3 className="font-display font-bold text-base text-[#10283D] dark:text-white">
                🛡️ Pipe Velocity &amp; Standards Limits
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-300">
                Cross-references flow velocity against acoustic noise, erosion wear, and biofilm prevention thresholds from CIBSE, BS EN 806, and ASHRAE.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4: Who Finds These Tools Useful */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            4. Who Finds These Tools Useful?
          </h2>
          <ul className="list-disc pl-6 space-y-2 text-sm text-slate-700 dark:text-slate-300">
            <li>
              <strong>Building Services &amp; MEP Consultants:</strong> Preparing conceptual sizing, preliminary hydraulic calculations, and design verification reports.
            </li>
            <li>
              <strong>Mechanical &amp; Plumbing Contractors:</strong> Verifying pipe diameters, selecting appropriate nominal sizes on site, and checking friction head before ordering pipework.
            </li>
            <li>
              <strong>Facility Managers &amp; Plant Operators:</strong> Assessing capacity upgrades, diagnosing localized pressure drops, and evaluating pump sizing requirements.
            </li>
            <li>
              <strong>Engineering Students &amp; Educators:</strong> Exploring fluid mechanics principles, verifying textbook problems, and comparing empirical vs theoretical formulas.
            </li>
          </ul>
        </section>

        {/* Section 5: Practical Engineering Focus & Transparency */}
        <section className="space-y-4 text-sm sm:text-base text-slate-700 dark:text-slate-300 leading-relaxed">
          <h2 className="font-display font-bold text-xl text-[#10283D] dark:text-white">
            5. Our Focus on Practical Usability &amp; Transparency
          </h2>
          <p>
            Engineering calculations should be verifiable and transparent. In every calculation module on this site, formulas, fluid properties, roughness values (&epsilon;), and standard references are explicitly displayed. Users can inspect the exact mathematics driving every output—from the continuity equation to Swamee-Jain friction coefficients.
          </p>
          <p>
            In addition, every calculator includes an isolated, professional PDF Report feature that formats all user inputs, calculated values, and engineering references into a print-ready document suitable for design records and peer review.
          </p>
        </section>

        {/* Section 6: Independent Engineering Utility Notice */}
        <section className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 space-y-3">
          <h3 className="font-display font-bold text-sm text-[#10283D] dark:text-white uppercase tracking-wider">
            Independent Web Utility Notice
          </h3>
          <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
            PipeSizingCalculator.com is an independent web utility provided free of charge. We are not affiliated with, endorsed by, or acting as an official certifying body for BSI, CIBSE, ASME, ASTM, or ISO. All referenced engineering standards are mentioned for technical identification and standard compliance contexts. For full engineering terms and liability limitations, please review our{' '}
            <button
              onClick={() => handleNav('/disclaimer/', 'disclaimer')}
              className="text-[#087F9C] dark:text-[#39BFD3] underline font-semibold hover:opacity-80"
            >
              Engineering Disclaimer
            </button>{' '}
            and{' '}
            <button
              onClick={() => handleNav('/terms-and-conditions/', 'terms')}
              className="text-[#087F9C] dark:text-[#39BFD3] underline font-semibold hover:opacity-80"
            >
              Terms and Conditions
            </button>.
          </p>
        </section>

        {/* Navigation Quick Links */}
        <footer className="pt-6 border-t border-slate-100 dark:border-slate-700/80 flex flex-wrap gap-3 text-xs font-semibold">
          <button
            onClick={() => handleNav('/', 'calculators', 'water')}
            className="px-4 py-2.5 bg-[#087F9C] text-white rounded-xl hover:bg-teal-700 transition-colors"
          >
            Launch Water Pipe Sizing &rarr;
          </button>
          <button
            onClick={() => handleNav('/contact-us/', 'contact')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Contact &amp; Feedback &rarr;
          </button>
          <button
            onClick={() => handleNav('/guides/', 'guides')}
            className="px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Engineering Guides &rarr;
          </button>
        </footer>
      </article>
    </div>
  );
};
